<script setup lang="ts">
import type { BaseTextKey } from '@n8n/i18n';
import { useLogStreamingStore } from '../logStreaming.store';
import { computed } from 'vue';
import { useI18n } from '@n8n/i18n';

import { N8nCheckbox, N8nIcon, N8nTooltip } from '@n8n/design-system';

interface Props {
	destinationId?: string;
	readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	destinationId: 'defaultDestinationId',
	readonly: false,
});

const emit = defineEmits<{
	input: [];
	change: [payload: { name: string; node: string; value: boolean }];
}>();

const i18n = useI18n();
const logStreamingStore = useLogStreamingStore();

const anonymizeAuditMessages = computed(
	() => logStreamingStore.items[props.destinationId]?.destination.anonymizeAuditMessages,
);

function onInput() {
	emit('input');
}

function onCheckboxChecked(eventName: string, checked: boolean) {
	logStreamingStore.setSelectedInGroup(props.destinationId, eventName, checked);
}

function anonymizeAuditMessagesChanged(value: boolean) {
	logStreamingStore.items[props.destinationId].destination.anonymizeAuditMessages = value;
	emit('change', { name: 'anonymizeAuditMessages', node: props.destinationId, value });
}

function groupLabelName(t: string): string {
	return i18n.baseText(`settings.log-streaming.eventGroup.${t}` as BaseTextKey) ?? t;
}

function groupLabelInfo(t: string): string | undefined {
	const labelInfo = `settings.log-streaming.eventGroup.${t}.info`;
	const infoText = i18n.baseText(labelInfo as BaseTextKey);
	if (infoText === labelInfo || infoText === '') return;
	return infoText;
}

function eventLabelName(event: { name: string; label: string }): string {
	const key = `settings.log-streaming.eventName.${event.name}`;
	return i18n.exists(key) ? i18n.baseText(key as BaseTextKey) : event.label;
}
</script>

<template>
	<div>
		<div
			v-for="group in logStreamingStore.items[destinationId]?.eventGroups"
			:key="group.name"
			shadow="never"
		>
			<!-- <template #header> -->
			<N8nCheckbox
				:model-value="group.selected"
				:indeterminate="group.indeterminate"
				:disabled="readonly"
				@update:model-value="
					(value) => {
						onInput();
						onCheckboxChecked(group.name, value);
					}
				"
			>
				<template #label>
					<strong>{{ groupLabelName(group.name) }}</strong>
					<N8nTooltip
						v-if="groupLabelInfo(group.name) !== undefined"
						placement="top"
						:content-class="$style.tooltipPopper"
						class="ml-xs"
					>
						<N8nIcon icon="circle-help" size="small" class="ml-4xs" />
						<template #content>
							{{ groupLabelInfo(group.name) }}
						</template>
					</N8nTooltip>
				</template>
			</N8nCheckbox>
			<N8nCheckbox
				v-if="group.name === 'n8n.audit'"
				:model-value="anonymizeAuditMessages"
				:disabled="readonly"
				@update:model-value="
					(value) => {
						onInput();
						anonymizeAuditMessagesChanged(value);
					}
				"
			>
				<template #label>
					{{ i18n.baseText('settings.log-streaming.tab.events.anonymize') }}
					<N8nTooltip placement="top" :content-class="$style.tooltipPopper">
						<N8nIcon icon="circle-help" size="small" class="ml-4xs" />
						<template #content>
							{{ i18n.baseText('settings.log-streaming.tab.events.anonymize.info') }}
						</template>
					</N8nTooltip>
				</template>
			</N8nCheckbox>
			<!-- </template> -->
			<ul :class="$style.eventList">
				<li v-for="event in group.children" :key="event.name" :class="`${$style.eventListItem}`">
					<N8nCheckbox
						:model-value="event.selected || group.selected"
						:indeterminate="event.indeterminate"
						:disabled="readonly"
						@update:model-value="
							(value) => {
								onInput();
								onCheckboxChecked(event.name, value);
							}
						"
					>
						<template #label>
							{{ eventLabelName(event) }}
							<N8nTooltip placement="top" :content-class="$style.tooltipPopper">
								<template #content>
									{{ event.name }}
								</template>
							</N8nTooltip>
						</template>
					</N8nCheckbox>
				</li>
			</ul>
		</div>
	</div>
</template>

<style lang="scss" module>
.eventListCard {
	margin-left: 1em;
}

.eventList {
	height: auto;
	overflow-y: auto;
	padding: 0;
	margin: 0;
	list-style: none;
}
.eventList .eventListItem {
	display: flex;
	align-items: center;
	justify-content: left;
	margin: 10px;
	color: var(--color--primary);
}

.eventListItemDisabled > {
	label > {
		span > {
			span {
				background-color: transparent !important;
				&:after {
					border-color: rgb(54, 54, 54) !important;
				}
			}
		}
	}
}

.eventList .eventListItem + .listItem {
	margin-top: 10px;
}
</style>
